import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Api, Http } from 'src/api';
import { authQueries, useProfileQuery } from 'src/api/queries';
import { AuthLoginRequest, AuthLoginResponse } from 'src/api/services/AuthClient';
import { Broadcast } from 'src/broadcast';
import { pushNotification } from 'src/lib/services/push-notification';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { JsonWrapper } from '../JsonWrapper';
import { AuthJwtObject, jwtStorageKey } from './Auth.types';
import { AuthUtils } from './Auth.utils';
import { isAuthJwtObject } from './Auth.guards';
import { useUnsetVisitedPageCookie } from 'src/helpers/useUnsetVisitedPageCookie';
import { cookieKey } from 'src/types/global';
import { appRoutes } from 'src/routes/routes.types';
import { useRedirectMiddleware } from 'src/routes/RedirectMiddleware';
import { useAuthStore } from './Auth.store';
import { useDecoratedPostHog } from 'src/providers/posthog';
import { useCanny } from 'src/lib/services/canny';
import { hubspot } from 'src/lib/services/hubspot';
import { useUserGuiding } from 'src/lib/services/user-guiding';
import { LocalStorage } from 'src/services/LocalStorage';
import { StorageService } from 'src/services/StorageService';
import { useGlobalLoadingStore } from 'src/stores';
import Logger from 'src/utils/logger';

let timeoutId: NodeJS.Timeout | null = null;

const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const redirectMiddleware = useRedirectMiddleware();
  const { setIsLoggedIn, setAppStarted, beginSession, isWebView } = useAuthStore();
  const postHog = useDecoratedPostHog();
  const userGuiding = useUserGuiding();
  const { setGlobalLoading } = useGlobalLoadingStore();

  const removeVisited = useUnsetVisitedPageCookie();

  const query = useProfileQuery({ enabled: false });
  const canny = useCanny();

  const isLoaded = query.isSuccess || query.isError,
    isLoading = query.isLoading,
    isInitialLoading = query.isInitialLoading;

  const startSession = async (jwt: string | AuthJwtObject) => {
    const token = isAuthJwtObject(jwt) ? jwt : AuthUtils.make(jwt);

    // Captured BEFORE anything below writes the flag, and read from the store rather than the
    // hook closure so it cannot be stale. This is what separates "a session began" from "the
    // session we already had was refreshed" — see beginSession() below.
    const wasLoggedOut = !useAuthStore.getState().isLoggedIn;

    AuthUtils.setJwt(token);
    Http.setBearer(token.token);
    if (document.visibilityState !== 'hidden') {
      Broadcast.user.connect(token.token);
    }

    if (pushNotification.isSupported()) {
      pushNotification.init();
    }

    const data = await queryClient.fetchQuery({ queryKey: query.queryKey, queryFn: query.queryFn });

    const fullName = joinExistingValues([data.first_name, data.last_name], ' ');
    const isImpersonated = !!AuthUtils.getPayload()?.impersonator;

    // Skip analytics tracking for impersonated users to avoid polluting data
    // and to prevent slow third-party services from blocking the session
    if (!isImpersonated) {
      postHog.identify(data.id, {
        email: data.email,
        company_id: data.company.id,
        company_has_brands: data.company.meta.has_brands,
        company_has_requests: data.company.meta.has_requests,
        company_submitted_requests: data.company.meta.submitted_requests,
        company_has_pro_plan: data.company.meta.has_pro_plan,
        company_has_subscriptions: data.company.meta.has_subscriptions,
      });
      postHog.group('company', data.company.id, {
        name: data.company.name,
      });

      canny.identify({
        id: data.id,
        name: fullName.substring(0, 50), // canny fails to identify if name is longer than 50 characters
        email: data.email,
        created: new Date(data.created_at).toISOString(),
        customFields: {
          userType: data.type,
        },
        companies: [
          {
            id: data.company.id,
            name: data.company.name,
            monthlySpend: data.company.monthly_spend,
          },
        ],
      });

      // HubSpot identify - don't await to prevent blocking (use fire-and-forget)
      hubspot.identify(data.email).then((identified) => {
        if (identified) {
          hubspot.trackPageView();
        } else {
          Logger.warn('HubSpot identification failed, skipping page view tracking', 'useAuth');
        }
      }).catch((e) => Logger.error('HubSpot identify error', 'useAuth', e));

      userGuiding.loadAndIdentify().catch((e) => Logger.error('Failed to load user guiding SDK', 'useAuth', e));
    } else {
      Logger.info('Skipping analytics for impersonated session', 'useAuth');
    }

    setIsLoggedIn(true);
    // A session has been ESTABLISHED FROM A LOGGED-OUT STATE, past the awaited profile fetch.
    // That pair of conditions is what the WebView recovery budget is handed back on.
    //
    // `wasLoggedOut` is load-bearing, not a micro-optimisation. Without it this fires on the
    // boot `refresh()` success path too, which runs ONCE PER DOCUMENT — and the budget it
    // resets exists to bound a loop made of document loads, so a once-per-document reset is no
    // bound at all. It would also fire in the OTHER live WebViews: native writes CF's JWT key
    // directly (mobile-app useWebviewAuth.ts:219 → 'jwt-becreatives'), which raises a `storage`
    // event in the sibling preloader documents and lands in handleStorageTokenChange below.
    // Requiring a logged-out start excludes both: a token being refreshed under an existing
    // session is not a new session. An AUTH_TOKEN reply cannot reach here at all — it goes to
    // Auth.store.handleWebViewToken, which never calls startSession.
    if (wasLoggedOut) {
      beginSession();
    }
    setGlobalLoading(false);

    if (isImpersonated) {
      return;
    }

    // Store minimal info for quicker login from login page
    // SECURITY: Only store non-sensitive display data, no email or password status
    LocalStorage.set(
      'loggedInUser',
      JSON.stringify({
        name: `${data.first_name}${data.last_name ? ` ${data.last_name}` : ''}`,
        avatar: data.avatar?.generated_conversions.square_200 ?? data.avatar?.index_url,
        // Store first initial of email for display hint only
        emailHint: data.email?.includes('@')
          ? `${data.email.charAt(0)}***@${data.email.split('@')[1]}`
          : undefined,
      }),
    );
  };

  const endSession = async () => {
    // Note: Http.setLoggingOut(true) should be called by the caller (logout/refresh)
    // before calling endSession to suppress error toasts early

    queryClient.cancelQueries();
    queryClient.removeQueries();

    Http.setBearer('');
    Broadcast.user.disconnect();
    postHog.reset();
    userGuiding.shutdown();

    Object.values(cookieKey).forEach((key) => removeVisited(key));

    // SECURITY: Comprehensive cleanup of all user data from browser storage
    // This clears JWT, user info, session data, cached content, and IndexedDB
    try {
      await StorageService.fullCleanup();
    } catch (e) {
      Logger.error('Failed to cleanup storage during logout', 'useAuth', e);
    }

    setIsLoggedIn(false);

    // Don't reset logout flag here - it will be reset when user logs in again
    // This ensures all pending requests during/after logout are suppressed
  };

  const login = async (data: AuthLoginRequest): Promise<AuthLoginResponse> => {
    // Reset logout flag when logging in
    Http.setLoggingOut(false);

    const { access_token } = await Api.auth.login(data);

    // Show global loading only after successful authentication
    // This prevents the login form from being unmounted during failed attempts
    setGlobalLoading(true);

    await startSession(access_token);

    return { access_token };
  };

  const logout = async (): Promise<void> => {
    // Suppress error toasts BEFORE any logout API calls
    Http.setLoggingOut(true);

    try {
      await Api.auth.logout();
    } catch (e) {
      // Ignore logout API errors - we're logging out anyway
      Logger.debug('Logout API call failed (expected if session expired)', 'useAuth', e);
    }

    await endSession();
  };

  const refresh = async () => {
    if (AuthUtils.isExpired()) {
      // JWT cleanup is handled by StorageService.fullCleanup() in endSession
      Http.setLoggingOut(true);
      await endSession();
      setAppStarted(true);
      return;
    }

    await Api.auth
      .refresh()
      .then(async ({ access_token }) => {
        if (!access_token) {
          Http.setLoggingOut(true);
          await endSession();

          return;
        }

        // Add WebView token refresh notification
        if (isWebView) {
          window?.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'WEBVIEW_AUTH_TOKEN',
              token: access_token,
            }),
          );
        }

        await startSession(access_token);
      })
      .catch(async () => {
        Http.setLoggingOut(true);
        await endSession();
      })
      .finally(() => setAppStarted(true));
  };

  const handleStorageTokenChange = async (e: StorageEvent): Promise<void> => {
    if (e.key !== jwtStorageKey) {
      return;
    }

    if (e.oldValue && !e.newValue) {
      // Means user has logged out
      Http.setLoggingOut(true);
      await endSession();
      navigate(appRoutes.login);
      return;
    }

    const newJwt = JsonWrapper.toJson(e.newValue);
    if (!isAuthJwtObject(newJwt)) {
      return;
    }

    if (!e.oldValue) {
      // Means user has logged in
      await startSession(newJwt);
      await redirectMiddleware(appRoutes.dashboard);
      return;
    }

    if (e.oldValue && e.newValue) {
      // Means token has been refreshed
      const oldJwt = JsonWrapper.toJson(e.oldValue);

      if (
        isAuthJwtObject(oldJwt) &&
        !AuthUtils.isExpired(newJwt) &&
        oldJwt.token !== newJwt.token
      ) {
        await startSession(newJwt);
      }
    }
  };

  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'hidden') {
      timeoutId = setTimeout(() => {
        Broadcast.user.disconnect();
      }, 5000);

      return;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!Broadcast.user.isConnected() && !AuthUtils.isExpired()) {
      Broadcast.user.connect(AuthUtils.getJwt().token);
      queryClient.invalidateQueries({
        queryKey: [authQueries.notifications],
      });
      queryClient.invalidateQueries({
        queryKey: [authQueries.unreadNotifications],
      });
      queryClient.invalidateQueries({
        queryKey: [authQueries.requestCounts],
      });
    }
  };

  const mount = () => {
    window.addEventListener('storage', handleStorageTokenChange);
    window.addEventListener('visibilitychange', handleVisibilityChange);
  };

  const dismount = () => {
    window.removeEventListener('storage', handleStorageTokenChange);
    window.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  return {
    isLoaded,
    isLoading,
    isInitialLoading,
    login,
    logout,
    refresh,
    startSession,
    endSession,
    mount,
    dismount,
  };
};

export { useAuth };
